<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import { CkAlert, CkStatTile } from '@ck/ui'
import { dashboardApi, workOrdersApi } from '@ck/api-client'
import { formatDateTime } from '@ck/domain'
import FilaVeiculos from '../components/FilaVeiculos.vue'

const route = useRoute()
const q = computed(() => typeof route.query.q === 'string' ? route.query.q : '')

const summaryQuery = useQuery({ queryKey: ['dashboard-summary'], queryFn: () => dashboardApi.summary() })
const activityQuery = useQuery({ queryKey: ['dashboard-activity'], queryFn: () => dashboardApi.activity(20) })
const ordersQuery = useQuery(computed(() => ({ queryKey: ['work-orders', q.value], queryFn: () => workOrdersApi.list(q.value) })))
const orders = computed(() => ordersQuery.data.value?.items ?? [])

</script>

<template>
  <div>
    <div class="page-head"><h1>Painel</h1><p>Visão geral da oficina</p></div>

    <CkAlert v-if="summaryQuery.isError.value" tone="error" class="alert">Não foi possível carregar os indicadores.</CkAlert>
    <section class="kpis" aria-label="Indicadores">
      <CkStatTile :value="summaryQuery.data.value?.vehiclesInShop ?? '—'" label="Veículos na oficina" tone="accent"/>
      <CkStatTile :value="summaryQuery.data.value?.openWorkOrders ?? '—'" label="OS abertas"/>
      <CkStatTile :value="summaryQuery.data.value?.deliveriesThisMonth ?? '—'" label="Entregas no mês" tone="ok"/>
      <CkStatTile :value="summaryQuery.data.value?.rejectedItems ?? '—'" label="Itens reprovados" tone="fail"/>
    </section>

    <CkAlert v-if="ordersQuery.isError.value" tone="error" class="alert">Não foi possível carregar as ordens de serviço.</CkAlert>
    <div class="grid">
      <section>
        <div class="section-head"><h2>Fila de veículos</h2></div>
        <FilaVeiculos :work-orders="orders"/>
      </section>
      <section>
        <div class="section-head"><h2>Atividade recente</h2></div>
        <div class="activity-card">
          <p v-if="activityQuery.isError.value" class="empty">Não foi possível carregar a atividade recente.</p>
          <p v-else-if="activityQuery.data.value?.length === 0" class="empty">Nenhuma atividade registrada ainda.</p>
          <article v-for="event in activityQuery.data.value" :key="event.id" class="event">
            <span class="dot" aria-hidden="true"/>
            <div>
              <p>{{ event.description }}</p>
              <small class="ck-code">{{ formatDateTime(event.occurredAt) }} · {{ event.workOrderNumber }}</small>
            </div>
          </article>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.page-head { margin-bottom: 20px; }
.page-head h1 { margin: 0; font-family: var(--font-display); font-size: 26px; font-weight: 800; }
.page-head p { margin: 4px 0 0; color: var(--color-text-faint); font-size: 13px; }
.alert { margin-bottom: 16px; }
.kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 24px; }
.grid { display: grid; grid-template-columns: 1.7fr 1fr; gap: 20px; align-items: start; }
.section-head { margin-bottom: 12px; }
.section-head h2 { margin: 0; font-family: var(--font-display); font-size: 17px; font-weight: 700; }
.activity-card { padding: 18px 20px; border: 1px solid var(--color-border); border-radius: var(--radius-lg); background: var(--color-surface); }
.event { display: flex; gap: 12px; padding-bottom: 16px; }
.event:last-child { padding-bottom: 0; }
.event .dot { flex-shrink: 0; width: 9px; height: 9px; margin-top: 5px; border-radius: 99px; background: var(--ck-orange-500); }
.event p { margin: 0; font-size: 13.5px; line-height: 1.4; }
.event small { color: var(--color-text-faint); font-size: 11px; }
.empty { color: var(--color-text-muted); font-size: 13px; }
@media (max-width: 950px) { .kpis { grid-template-columns: repeat(2, 1fr); } .grid { grid-template-columns: 1fr; } }
</style>
